import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'
import Layout from '@/views/Layout.vue'
import { useAuthStore } from '@/stores/auth'
import { hasAnyPermission } from '@/utils/permission'

const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/Login.vue')
  },
  {
    // 平台超管后台（独立认证，页面内部校验 platformToken）
    path: '/saas-admin',
    name: 'SaasAdmin',
    component: () => import('@/views/SaasAdmin.vue'),
    meta: { title: '平台管理', public: true }
  },
  {
    path: '/terms',
    name: 'Terms',
    component: () => import('@/views/legal/Terms.vue'),
    meta: { title: '用户协议', public: true }
  },
  {
    path: '/privacy',
    name: 'Privacy',
    component: () => import('@/views/legal/Privacy.vue'),
    meta: { title: '隐私政策', public: true }
  },
  {
    // 客户在线签约页（公开访问，无需登录）
    path: '/sign/:token',
    name: 'SignPage',
    component: () => import('@/views/sign/SignPage.vue'),
    meta: { title: '合同签约', public: true }
  },
  {
    // 客户报价单分享页（公开访问，首次打开绑定设备）
    path: '/q/:token',
    name: 'QuotationShare',
    component: () => import('@/views/share/QuotationSharePage.vue'),
    meta: { title: '报价单', public: true }
  },
  {
    path: '/',
    component: Layout,
    redirect: '/dashboard',
    children: [
      {
        path: 'dashboard',
        name: 'Dashboard',
        component: () => import('@/views/Dashboard.vue'),
        meta: { title: '首页' }
      },
      {
        path: 'sales-orders',
        name: 'SalesOrderList',
        component: () => import('@/views/sales/SalesOrderList.vue'),
        meta: { title: '销售订单', permission: 'sales:read' }
      },
      {
        path: 'quotations',
        name: 'QuotationList',
        component: () => import('@/views/sales/QuotationList.vue'),
        meta: { title: '报价管理', permission: 'sales:read' }
      },
      {
        path: 'referrers',
        name: 'ReferrerList',
        component: () => import('@/views/sales/ReferrerList.vue'),
        meta: { title: '介绍人管理' }
      },
      {
        path: 'sales-orders/create',
        name: 'SalesOrderCreate',
        component: () => import('@/views/sales/SalesOrderForm.vue'),
        meta: { title: '新增销售订单', permission: 'sales:create' }
      },
      {
        path: 'sales-orders/:id/edit',
        name: 'SalesOrderEdit',
        component: () => import('@/views/sales/SalesOrderForm.vue'),
        meta: { title: '编辑销售订单', permission: 'sales:update' }
      },
      {
        path: 'sales-orders/:id',
        name: 'SalesOrderDetail',
        component: () => import('@/views/sales/SalesOrderDetail.vue'),
        meta: { title: '销售订单详情', permission: 'sales:read' }
      },
      {
        path: 'customers',
        name: 'CustomerList',
        component: () => import('@/views/customer/CustomerList.vue'),
        meta: { title: '客户管理', permission: 'customer:read' }
      },
      {
        path: 'leads',
        name: 'LeadList',
        component: () => import('@/views/customer/LeadList.vue'),
        meta: { title: '线索管理', permission: 'lead:read' }
      },
      {
        path: 'customers/create',
        name: 'CustomerCreate',
        component: () => import('@/views/customer/CustomerForm.vue'),
        meta: { title: '新建客户', permission: 'customer:create' }
      },
      {
        path: 'customers/:id/edit',
        name: 'CustomerEdit',
        component: () => import('@/views/customer/CustomerForm.vue'),
        meta: { title: '编辑客户', permission: 'customer:update' }
      },
      {
        path: 'customers/:id',
        name: 'CustomerDetail',
        component: () => import('@/views/customer/CustomerDetail.vue'),
        meta: { title: '客户详情', permission: 'customer:read' }
      },
      {
        path: 'customers/pool',
        name: 'CustomerPool',
        component: () => import('@/views/customer/CustomerPool.vue'),
        meta: { title: '客户公海池', permission: 'customer:read' }
      },
      {
        path: 'customers/recycle',
        name: 'CustomerRecycle',
        component: () => import('@/views/customer/CustomerRecycle.vue'),
        meta: { title: '客户回收站', permission: 'customer:read' }
      },
      {
        path: 'products',
        name: 'ProductList',
        component: () => import('@/views/product/ProductList.vue'),
        meta: { title: '产品管理', permission: 'product:read' }
      },
      {
        path: 'products/logs',
        name: 'ProductLogs',
        component: () => import('@/views/product/ProductLogs.vue'),
        meta: { title: '商品修改记录', permission: 'product:log' }
      },
      {
        path: 'products/create',
        name: 'ProductCreate',
        component: () => import('@/views/product/ProductForm.vue'),
        meta: { title: '新建产品', permission: 'product:create' }
      },
      {
        path: 'products/:id/edit',
        name: 'ProductEdit',
        component: () => import('@/views/product/ProductForm.vue'),
        meta: { title: '编辑产品', permission: 'product:update' }
      },
      {
        path: 'suppliers',
        name: 'SupplierList',
        component: () => import('@/views/supplier/SupplierList.vue'),
        meta: { title: '供应商管理', permission: 'supplier:read' }
      },
      {
        path: 'logistics-companies',
        name: 'LogisticsCompanyList',
        component: () => import('@/views/supplier/LogisticsCompanyList.vue'),
        meta: { title: '物流公司管理' }
      },
      {
        path: 'suppliers/create',
        name: 'SupplierCreate',
        component: () => import('@/views/supplier/SupplierForm.vue'),
        meta: { title: '新建供应商', permission: 'supplier:create' }
      },
      {
        path: 'suppliers/:id/edit',
        name: 'SupplierEdit',
        component: () => import('@/views/supplier/SupplierForm.vue'),
        meta: { title: '编辑供应商', permission: 'supplier:update' }
      },
      {
        path: 'categories',
        name: 'CategoryList',
        component: () => import('@/views/product/CategoryList.vue'),
        meta: { title: '商品分类' }
      },
      {
        path: 'categories/create',
        name: 'CategoryCreate',
        component: () => import('@/views/product/CategoryForm.vue'),
        meta: { title: '新建分类' }
      },
      {
        path: 'categories/:id/edit',
        name: 'CategoryEdit',
        component: () => import('@/views/product/CategoryForm.vue'),
        meta: { title: '编辑分类' }
      },
      {
        path: 'hr/employees',
        name: 'EmployeeList',
        component: () => import('@/views/hr/EmployeeList.vue'),
        meta: { title: '员工管理', permission: 'employee:read' }
      },
      {
        path: 'hr/commissions',
        name: 'CommissionList',
        component: () => import('@/views/hr/CommissionList.vue'),
        meta: { title: '销售提成', permission: 'commission:read' }
      },
      {
        path: 'hr/salary',
        name: 'SalaryManager',
        component: () => import('@/views/hr/SalaryManager.vue'),
        meta: { title: '工资核算', permission: 'salary:read' }
      },
      {
        path: 'hr/performance',
        name: 'PerformanceEvaluation',
        component: () => import('@/views/hr/PerformanceEvaluation.vue'),
        meta: { title: '绩效评估', permission: 'performance:read' }
      },
      {
        path: 'hr/tools',
        name: 'ToolIssueList',
        component: () => import('@/views/hr/ToolIssueList.vue'),
        meta: { title: '工具领用', permission: 'tool:read' }
      },
      {
        path: 'contract',
        name: 'ContractManager',
        component: () => import('@/views/contract/ContractManager.vue'),
        meta: { title: '合同管理', permission: 'contract:read' }
      },
      {
        path: 'schedule',
        name: 'ScheduleCalendar',
        component: () => import('@/views/schedule/ScheduleCalendar.vue'),
        meta: { title: '日程管理', permission: 'schedule:read' }
      },
      {
        path: 'todos',
        name: 'TodoList',
        component: () => import('@/views/todo/TodoList.vue'),
        meta: { title: '任务中心', permission: 'todo:read' }
      },
      {
        path: 'todos/internal',
        name: 'InternalTodoList',
        component: () => import('@/views/todo/InternalTodoList.vue'),
        meta: { title: '内勤任务', permission: 'todo:read' }
      },
      {
        path: 'crm/grading',
        name: 'CustomerGrading',
        component: () => import('@/views/crm/CustomerGrading.vue'),
        meta: { title: '客户分级', permission: 'crm:read' }
      },
      {
        path: 'crm/churn-warning',
        name: 'ChurnWarning',
        component: () => import('@/views/crm/ChurnWarning.vue'),
        meta: { title: '流失预警', permission: 'crm:read' }
      },
      {
        path: 'finance',
        name: 'FinanceList',
        component: () => import('@/views/finance/FinanceList.vue'),
        meta: { title: '财务管理', permission: 'finance:read' }
      },
      {
        path: 'finance/payment-approval',
        name: 'PaymentApproval',
        component: () => import('@/views/finance/PaymentApproval.vue'),
        meta: {
          title: '收款审批',
          permissions: ['finance:read', 'finance:account-all', 'finance:payment-cashier-review', 'finance:payment-finance-review']
        }
      },
      {
        path: 'finance/reports',
        name: 'FinanceReport',
        component: () => import('@/views/finance/FinanceReport.vue'),
        meta: { title: '财务报表', permission: 'finance:report' }
      },
      {
        path: 'reports',
        name: 'ReportCenter',
        component: () => import('@/views/report/ReportCenter.vue'),
        meta: {
          title: '销售报表',
          permissions: [
            'report:read', 'finance:report',
            'report:sales-trend', 'report:product-rank', 'report:customer-rank',
            'report:stock-analysis',
            'report:employee-performance', 'report:invoice-summary',
            'report:profit', 'report:balance-sheet', 'report:cash-flow'
          ]
        }
      },
      {
        path: 'reports/purchase',
        name: 'PurchaseReport',
        component: () => import('@/views/report/PurchaseReport.vue'),
        meta: {
          title: '采购报表',
          permissions: [
            'report:read', 'finance:report',
            'report:purchase-trend', 'report:purchase-order-list',
            'report:purchase-product-rank', 'report:purchase-summary'
          ]
        }
      },
      {
        path: 'finance/expense-claims',
        name: 'ExpenseClaimList',
        component: () => import('@/views/finance/ExpenseClaimList.vue'),
        meta: { title: '费用报销', permission: 'finance:expense' }
      },
      {
        path: 'finance/expense-categories',
        name: 'ExpenseCategoryList',
        component: () => import('@/views/finance/ExpenseCategoryList.vue'),
        meta: { title: '报销类型', permission: 'finance:expense-category' }
      },
      {
        path: 'finance/other-incomes',
        name: 'OtherIncomeList',
        component: () => import('@/views/finance/OtherIncomeList.vue'),
        meta: { title: '其他收入', permission: 'finance:income' }
      },
      {
        path: 'finance/income-categories',
        name: 'IncomeCategoryList',
        component: () => import('@/views/finance/IncomeCategoryList.vue'),
        meta: { title: '收入科目', permission: 'finance:income-category' }
      },
      {
        path: 'finance/rebates',
        name: 'RebateList',
        component: () => import('@/views/finance/RebateList.vue'),
        meta: { title: '返点管理', permission: 'rebate:read' }
      },
      {
        path: 'returns',
        name: 'ReturnList',
        component: () => import('@/views/return/ReturnList.vue'),
        meta: { title: '退货管理', permission: 'return:read' }
      },
      {
        path: 'returns/create',
        name: 'ReturnCreate',
        component: () => import('@/views/return/ReturnForm.vue'),
        meta: { title: '新增退货单', permission: 'return:create' }
      },
      {
        path: 'returns/:id/edit',
        name: 'ReturnEdit',
        component: () => import('@/views/return/ReturnForm.vue'),
        meta: { title: '编辑退货单', permission: 'return:create' }
      },
      {
        path: 'returns/:id',
        name: 'ReturnDetail',
        component: () => import('@/views/return/ReturnDetail.vue'),
        meta: { title: '退货单详情', permission: 'return:read' }
      },
      {
        path: 'inventory/warehouses',
        name: 'WarehouseList',
        component: () => import('@/views/inventory/WarehouseList.vue'),
        meta: { title: '仓库管理', permission: 'warehouse:read' }
      },
      {
        path: 'inventory/stock-warning',
        name: 'StockWarning',
        component: () => import('@/views/inventory/StockWarning.vue'),
        meta: { title: '库存预警', permission: 'stock:warning' }
      },
      {
        path: 'hr/attendance',
        name: 'AttendanceManager',
        component: () => import('@/views/hr/AttendanceManager.vue'),
        meta: { title: '考勤管理', permission: 'attendance:read' }
      },
      {
        path: 'hr/approval-submit',
        name: 'HrApprovalSubmit',
        component: () => import('@/views/hr/ApprovalSubmit.vue'),
        meta: { title: '审批提交', permission: 'leave:create' }
      },
      {
        path: 'work-orders',
        name: 'WorkOrderList',
        component: () => import('@/views/work-order/WorkOrderList.vue'),
        meta: { title: '安装工单', permission: 'work_order:read' }
      },
      {
        path: 'work-orders/install-records',
        name: 'InstallRecordList',
        component: () => import('@/views/work-order/InstallRecordList.vue'),
        meta: { title: '安装记录', permission: 'work_order:read' }
      },
      {
        path: 'work-orders/install-daily',
        name: 'InstallDailySummary',
        component: () => import('@/views/work-order/InstallDailySummary.vue'),
        meta: { title: '安装日报', permission: 'work_order:read' }
      },
      {
        path: 'work-orders/install-pending',
        name: 'InstallPendingItems',
        component: () => import('@/views/work-order/InstallPendingItems.vue'),
        meta: { title: '未报安装预警', permission: 'work_order:read' }
      },
      {
        path: 'inventory/transfers',
        name: 'TransferList',
        component: () => import('@/views/inventory/TransferList.vue'),
        meta: { title: '仓库调拨', permission: 'transfer:read' }
      },
      {
        path: 'inventory/stocktakes',
        name: 'StocktakeList',
        component: () => import('@/views/inventory/StocktakeList.vue'),
        meta: { title: '库存盘点', permission: 'stocktake:read' }
      },
      {
        path: 'inventory/stocktakes/:id/count',
        name: 'StocktakeCount',
        component: () => import('@/views/inventory/StocktakeCount.vue'),
        meta: { title: '盘点录入', permission: 'stocktake:read' }
      },
      {
        path: 'inventory/inbounds',
        name: 'InboundList',
        component: () => import('@/views/inventory/InboundList.vue'),
        meta: { title: '入库管理', permission: 'inbound:read' }
      },
      {
        path: 'inventory/outbounds',
        name: 'OutboundList',
        component: () => import('@/views/inventory/OutboundList.vue'),
        meta: { title: '出库管理', permission: 'outbound:read' }
      },
      {
        path: 'inventory/stock',
        name: 'StockList',
        component: () => import('@/views/inventory/StockList.vue'),
        meta: { title: '库存查询', permission: 'stock:read' }
      },
      {
        path: 'inventory/flows',
        name: 'StockFlows',
        component: () => import('@/views/inventory/StockFlows.vue'),
        meta: { title: '库存流水', permission: 'stock:read' }
      },
      {
        path: 'inventory/locations',
        name: 'LocationList',
        component: () => import('@/views/inventory/LocationList.vue'),
        meta: { title: '库位管理', permission: 'location:read' }
      },
      {
        path: 'purchase-orders',
        name: 'PurchaseOrderList',
        component: () => import('@/views/purchase/PurchaseOrderList.vue'),
        meta: { title: '采购订单', permission: 'purchase:read' }
      },
      {
        path: 'purchase-orders/create',
        name: 'PurchaseOrderCreate',
        component: () => import('@/views/purchase/PurchaseOrderForm.vue'),
        meta: { title: '新建采购单', permission: 'purchase:create' }
      },
      {
        path: 'purchase-orders/edit/:id',
        name: 'PurchaseOrderEdit',
        component: () => import('@/views/purchase/PurchaseOrderForm.vue'),
        meta: { title: '编辑采购单', permission: 'purchase:update' }
      },
      {
        // 已合并入导入导出管理（单据导入），保留重定向防止旧书签 404
        path: 'purchase-import',
        redirect: '/system/import-export'
      },
      {
        path: 'finance/invoices',
        name: 'InvoiceManager',
        component: () => import('@/views/finance/InvoiceManager.vue'),
        meta: { title: '发票管理', permission: 'invoice:read' }
      },
      {
        path: 'finance/installments',
        name: 'InstallmentManager',
        component: () => import('@/views/finance/InstallmentManager.vue'),
        meta: { title: '分期管理', permission: 'finance:read' }
      },
      {
        path: 'finance/reconciliations',
        name: 'CustomerReconciliation',
        component: () => import('@/views/finance/CustomerReconciliation.vue'),
        meta: { title: '客户对账', permission: 'finance:read' }
      },
      {
        path: 'finance/supplier-reconciliations',
        name: 'SupplierReconciliation',
        component: () => import('@/views/finance/SupplierReconciliation.vue'),
        meta: { title: '供应商对账', permission: 'finance:read' }
      },
      {
        path: 'finance/logistics-reconciliations',
        name: 'LogisticsReconciliation',
        component: () => import('@/views/finance/LogisticsReconciliation.vue'),
        meta: { title: '物流对账', permission: 'finance:read' }
      },
      {
        path: 'system/users',
        name: 'UserList',
        component: () => import('@/views/system/UserList.vue'),
        meta: { title: '用户管理', permission: 'user:manage' }
      },
      {
        path: 'system/roles',
        name: 'RoleList',
        component: () => import('@/views/system/RoleList.vue'),
        meta: { title: '角色管理', permission: 'system:role' }
      },
      {
        path: 'system/operation-logs',
        name: 'OperationLogs',
        component: () => import('@/views/system/OperationLogs.vue'),
        meta: { title: '操作日志', permission: 'system:log' }
      },
      {
        path: 'system/images',
        name: 'ImageManager',
        component: () => import('@/views/system/ImageManager.vue'),
        meta: { title: '图片管理', permission: 'system:image' }
      },
      {
        path: 'system/import-export',
        name: 'ImportExportManager',
        component: () => import('@/views/system/ImportExportManager.vue'),
        meta: { title: '导入导出', permission: 'import:read' }
      },
      {
        path: 'system/wework-config',
        name: 'WeworkConfig',
        component: () => import('@/views/system/WeworkConfig.vue'),
        meta: { title: '企业微信配置', permission: 'system:admin' }
      },
      {
        path: 'system/wework-rules',
        name: 'WeworkRules',
        component: () => import('@/views/system/WeworkRules.vue'),
        meta: { title: '企微消息规则', permission: 'system:admin' }
      },
      {
        path: 'system/settings',
        name: 'SystemSettings',
        component: () => import('@/views/system/Settings.vue'),
        meta: { title: '系统设置', permission: 'system:admin' }
      },
      {
        // 期初建账向导（新租户初始化引导）
        path: 'system/setup-wizard',
        name: 'SetupWizard',
        component: () => import('@/views/system/SetupWizard.vue'),
        meta: { title: '建账向导', permission: 'system:admin' }
      },
      {
        path: 'system/backup',
        name: 'DataBackup',
        component: () => import('@/views/system/DataBackup.vue'),
        meta: { title: '数据备份', permission: 'system:backup' }
      },
      {
        // 打印模板管理（系统管理一级菜单：拖拽模板列表 + 经典 A4 模板编辑）
        path: 'system/print-templates',
        name: 'PrintTemplates',
        component: () => import('@/views/system/PrintTemplates.vue'),
        meta: { title: '打印模板', permissions: ['system:setting', 'system:admin'] }
      },
      {
        // hiprint 打印模板设计器（拖拽版式，多模板）
        path: 'system/print-designer',
        name: 'HiprintPrintDesigner',
        component: () => import('@/views/system/HiprintDesigner.vue'),
        meta: { title: '打印模板设计器', permissions: ['system:setting', 'system:admin'] }
      },
      {
        path: 'system/install-category-rates',
        name: 'InstallCategoryConfig',
        component: () => import('@/views/system/InstallCategoryConfig.vue'),
        meta: { title: '安装类别费率', permission: 'install_rate:manage' }
      },
      {
        // 已合并入导入导出管理（单据导入），保留重定向防止旧书签 404
        path: 'qinsilk-import',
        redirect: '/system/import-export'
      },
      {
        path: 'ai-chat',
        name: 'AiChat',
        component: () => import('@/views/ai/AiChat.vue'),
        meta: { title: 'AI助手', permission: 'ai:use' }
      },
      {
        path: 'ai-growth',
        name: 'AiGrowth',
        component: () => import('@/views/ai-growth/AiGrowth.vue'),
        meta: { title: 'AI获客', permission: 'ai:growth' }
      },
      {
        path: 'crm/dashboard',
        name: 'CrmDashboard',
        component: () => import('@/views/crm/CrmDashboard.vue'),
        meta: { title: 'CRM仪表盘', permission: 'crm:read' }
      },
      {
        path: 'crm/opportunities',
        name: 'OpportunityList',
        component: () => import('@/views/crm/OpportunityList.vue'),
        meta: { title: '商机管理', permission: 'crm:read' }
      },
      {
        path: 'crm/follow-up-tasks',
        name: 'FollowUpTaskList',
        component: () => import('@/views/crm/FollowUpTaskList.vue'),
        meta: { title: '跟进任务', permission: 'crm:read' }
      }
    ]
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes
})

router.beforeEach((to, _from, next) => {
  // 公开路由（在线签约页）直接放行
  if (to.meta.public) {
    next()
    return
  }
  const token = localStorage.getItem('token')
  if (to.path !== '/login' && !token) {
    next(`/login?redirect=${encodeURIComponent(to.fullPath)}`)
    return
  }

  const authStore = useAuthStore()
  // 已登录但用户信息缺失（刷新页面/重新打开）：后台补拉，失败跳登录
  if (to.path !== '/login' && token && !authStore.userInfo) {
    authStore.fetchUserInfo().catch(() => {
      authStore.logout(to.fullPath)
    })
  }

  // 权限检查：如果路由配置了 permission 或 permissions（任一满足），检查当前用户是否有该权限
  const requiredPermission = to.meta.permission as string | undefined
  const requiredPermissions = (to.meta.permissions as string[] | undefined) || (requiredPermission ? [requiredPermission] : [])
  if (requiredPermissions.length > 0 && token) {
    // 如果用户信息还没加载，先加载
    if (!authStore.userInfo) {
      authStore.fetchUserInfo().then(() => {
        if (hasAnyPermission(requiredPermissions)) {
          next()
        } else {
          next('/dashboard')
        }
      }).catch(() => {
        next(`/login?redirect=${encodeURIComponent(to.fullPath)}`)
      })
      return
    }

    if (hasAnyPermission(requiredPermissions)) {
      next()
    } else {
      next('/dashboard')
    }
    return
  }

  next()
})

export default router
